// src/api.ts
import axios, { AxiosInstance } from "axios";
import { Capacitor } from "@capacitor/core";

const LOCAL_BACKEND_PORT = 8001;

if (import.meta.env.PROD && !import.meta.env.VITE_API_URL) {
  throw new Error(
    "VITE_API_URL is undefined. Set it in your env."
  );
}

const baseURL = Capacitor.isNativePlatform()
  ? import.meta.env.VITE_API_URL
  : import.meta.env.VITE_API_URL || `http://localhost:${LOCAL_BACKEND_PORT}`;

const isNative = Capacitor.isNativePlatform();

// ---------------------------------------------------------------------
// Access token storage
//
// WEB: memory only
// NATIVE: retain current localStorage behavior for now
// ---------------------------------------------------------------------

let memoryAccessToken: string | null = null;

// One-time migration for existing WEB users.
// Pick up their old stored token, move it into memory, then delete it.
if (!isNative && typeof window !== "undefined") {
  try {
    memoryAccessToken =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      null;

    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
  } catch {
    // ignore storage failures
  }
}

export function getAccessToken(): string | null {
  if (memoryAccessToken) {
    return memoryAccessToken;
  }

  // Keep native behavior unchanged in this release.
  if (isNative && typeof window !== "undefined") {
    try {
      return (
        localStorage.getItem("token") ||
        sessionStorage.getItem("token") ||
        null
      );
    } catch {
      return null;
    }
  }

  return null;
}

export function setAccessToken(token: string | null | undefined) {
  memoryAccessToken = token || null;

  if (typeof window === "undefined") {
    return;
  }

  try {
    if (isNative) {
      // Preserve native behavior for now.
      if (memoryAccessToken) {
        localStorage.setItem("token", memoryAccessToken);
      } else {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
      }
    } else {
      // WEB MUST NEVER persist the access token.
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
    }
  } catch {
    // ignore
  }
}

export function clearAccessToken() {
  setAccessToken(null);
}

const NATIVE_REFRESH_SERVER = "satstacker_refresh_v1";

async function getNativeRefreshCredentials() {
  if (!isNative) return null;

  try {
    const { NativeBiometric } = await import(
      "@capgo/capacitor-native-biometric"
    );

    const creds = await NativeBiometric.getCredentials({
      server: NATIVE_REFRESH_SERVER,
    });

    if (!creds?.password) return null;

    return {
      username: creds?.username || "",
      refreshToken: creds.password,
    };
  } catch {
    return null;
  }
}

export async function saveNativeRefreshToken(
  refreshToken: string | null | undefined,
  username = ""
) {
  if (!isNative || !refreshToken) return;

  const { NativeBiometric } = await import(
    "@capgo/capacitor-native-biometric"
  );

  await NativeBiometric.setCredentials({
    server: NATIVE_REFRESH_SERVER,
    username,
    password: refreshToken,
  });
}

export async function deleteNativeRefreshToken() {
  if (!isNative) return;

  try {
    const { NativeBiometric } = await import(
      "@capgo/capacitor-native-biometric"
    );

    await NativeBiometric.deleteCredentials({
      server: NATIVE_REFRESH_SERVER,
    });
  } catch {
    // No saved credential (or plugin unavailable) is fine.
  }
}

// ---------------------------------------------------------------------
// Main API client
// ---------------------------------------------------------------------

const api: AxiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Separate client used ONLY for refresh.
// It has no auth interceptors, so an expired ACCESS token can never
// accidentally be sent as the REFRESH token.
const refreshClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// ---------------------------------------------------------------------
// Central refresh
// ---------------------------------------------------------------------

let refreshPromise: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  let nativeRefreshError: unknown = null;
  let nativeCreds: { username: string; refreshToken: string } | null = null;

  // NATIVE: prefer the secure refresh credential first.
  // This keeps NativeBiometric and the server-side rotated cookie in sync.
  if (isNative) {
    nativeCreds = await getNativeRefreshCredentials();

    if (nativeCreds?.refreshToken) {
      try {
        const { data } = await refreshClient.post(
          "/auth/refresh",
          { refresh_token: nativeCreds.refreshToken }
        );

        const token = data?.access_token;

        if (!token) {
          throw new Error("Native refresh produced no access token");
        }

        setAccessToken(token);

        if (data?.refresh_token) {
          await saveNativeRefreshToken(
            data.refresh_token,
            nativeCreds.username
          );
        }

        return token;
      } catch (err: any) {
        nativeRefreshError = err;

        // A 401 means the saved refresh credential is no longer usable.
        // Remove it so we do not keep replaying a revoked/expired token.
        if (err?.response?.status === 401) {
          await deleteNativeRefreshToken();
          nativeCreds = null;
        }
      }
    }
  }

  // WEB: normal HttpOnly-cookie refresh.
  // NATIVE: fallback for users who did not opt into secure saved login.
  try {
    const { data } = await refreshClient.post(
      "/auth/refresh",
      {}
    );

    const token = data?.access_token;

    if (!token) {
      throw new Error("Refresh response did not contain access_token");
    }

    setAccessToken(token);

    // If this native device already had a secure saved-login credential,
    // keep it synchronized with the rotated refresh token returned here.
    if (isNative && nativeCreds?.refreshToken && data?.refresh_token) {
      await saveNativeRefreshToken(
        data.refresh_token,
        nativeCreds.username
      );
    }

    return token;
  } catch (cookieErr) {
    throw nativeRefreshError || cookieErr || new Error("Unable to refresh session");
  }
}

export async function refreshAccessToken(): Promise<string> {
  // Prevent five simultaneous 401s from creating five refresh requests.
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = performRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export async function logoutSession(): Promise<void> {
  let nativeRefreshToken: string | null = null;

  if (isNative) {
    const creds = await getNativeRefreshCredentials();
    nativeRefreshToken = creds?.refreshToken || null;
  }

  try {
    await refreshClient.post(
      "/auth/logout",
      nativeRefreshToken
        ? { refresh_token: nativeRefreshToken }
        : {}
    );
  } catch {
    // Local logout still proceeds if the network/server is unavailable.
  } finally {
    if (isNative) {
      await deleteNativeRefreshToken();
    }

    clearAccessToken();
  }
}

// ---------------------------------------------------------------------
// Request interceptor
// ---------------------------------------------------------------------

api.interceptors.request.use((cfg) => {
  const url = String(cfg.url || "");

  // These endpoints should NEVER receive the current access JWT.
  const skipBearer =
    /\/auth\/refresh(?:$|\?)/.test(url) ||
    /\/auth\/logout(?:$|\?)/.test(url) ||
    /\/auth\/firebase(?:$|\?)/.test(url) ||
    /\/token(?:$|\?)/.test(url);

  if (!skipBearer) {
    const token = getAccessToken();

    if (token) {
      cfg.headers = cfg.headers || {};
      (cfg.headers as any).Authorization = `Bearer ${token}`;
    }
  }

  return cfg;
});

// ---------------------------------------------------------------------
// Response interceptor: refresh once on 401 and retry
// ---------------------------------------------------------------------

api.interceptors.response.use(
  (response) => response,

  async (err) => {
    const status = err?.response?.status ?? 0;
    const cfg = err?.config ?? {};

    if (status !== 401) {
      return Promise.reject(err);
    }

    let path = "";

    try {
      const base =
        cfg.baseURL ||
        api.defaults.baseURL ||
        (typeof window !== "undefined"
          ? window.location.origin
          : "http://localhost");

      path = new URL(cfg.url || "", base).pathname;
    } catch {
      path = String(cfg.url || "");
    }

    // Authentication endpoints should report their own 401.
    if (
      path.includes("/auth/refresh") ||
      path.includes("/auth/firebase") ||
      /^\/token$/.test(path)
    ) {
      return Promise.reject(err);
    }

    if ((cfg as any)._retry) {
      return Promise.reject(err);
    }

    (cfg as any)._retry = true;

    try {
      const newToken = await refreshAccessToken();

      cfg.headers = cfg.headers || {};
      (cfg.headers as any).Authorization = `Bearer ${newToken}`;

      return api(cfg);
    } catch (refreshErr) {
      clearAccessToken();

      const here =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : "/";

      const onLogin =
        typeof window !== "undefined" &&
        window.location.pathname === "/login";

      const onPublicPage =
        typeof window !== "undefined" &&
        [
          "/",
          "/calculator",
          "/blog",
          "/pricing",
          "/faq",
          "/download",
          "/terms",
          "/privacy",
          "/review",
          "/share",
          "/bitcoin-dca",
          "/smart-dca",
        ].some(
          (p) =>
            window.location.pathname === p ||
            (p !== "/" &&
              window.location.pathname.startsWith(`${p}/`))
        );

      if (!onLogin && !onPublicPage && typeof window !== "undefined") {
        const w: any = window;

        if (!w.__redirectingToLogin) {
          w.__redirectingToLogin = true;

          window.location.replace(
            `/login?redirect=${encodeURIComponent(here)}`
          );

          setTimeout(() => {
            w.__redirectingToLogin = false;
          }, 1500);
        }
      }

      return Promise.reject(refreshErr);
    }
  }
);

export default api;